import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Pagination,
  Chip,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  TravelExplore as ScanIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { lotteryApi, Lottery, PaginatedLotteryResponse, CreateLotteryRequest } from '../services/lotteryService.ts';
import { tipoutApi } from '../services/tipoutService.ts';

// Bold borders used throughout this page's tables, and fixed column widths so the
// header row and every data/missing row line up in the same positions.
const LOTTERY_BORDER = '2px solid #7f0000';
const LOTTERY_BODY_BORDER = '2px solid #9e9e9e';
const LOTTERY_MISSING_BORDER = '2px solid #ffb300';
const LOTTERY_COLUMN_WIDTHS = ['8%', '9%', '7%', '7%', '7%', '7%', '8%', '9%', '8%', '9%', '11%', '10%'];

// The safe starts each day with $2000; Safe-Bank End is always derived from that
// fixed amount minus Safe Withdrawals rather than entered by hand.
const SAFE_START_AMOUNT = 2000;
const computeSafeBankEnd = (safeWithdrawals: number): number => SAFE_START_AMOUNT - (Number(safeWithdrawals) || 0);

// Deposit auto-calculates from these fields whenever one of them changes, but the
// field stays editable so a free/manual value can be typed in afterward.
const DEPOSIT_SOURCE_FIELDS = ['scratchTickets', 'looseSales', 'onlineSales', 'onlinePdOut', 'scratchPdOut'] as const;
const computeDeposit = (data: { scratchTickets?: string | number; looseSales?: number; onlineSales?: number; onlinePdOut?: number; scratchPdOut?: number }): number =>
  (Number(data.scratchTickets) || 0) + (Number(data.looseSales) || 0) + (Number(data.onlineSales) || 0) - (Number(data.onlinePdOut) || 0) + (Number(data.scratchPdOut) || 0);

const LotteryPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const { showError, showSuccess } = useSnackbar();
  const [lotteryEntries, setLotteryEntries] = useState<Lottery[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Current date state
  const [currentDate, setCurrentDate] = useState<string>('');
  const [dateLoading, setDateLoading] = useState(false);

  // "Scan for Missing Dates" (admin-only) creates a real blank entry (all amounts
  // $0, no notes) in the database for every missing date it finds, so the gaps
  // are permanently saved rather than a client-side placeholder that can vanish
  // on refresh/navigation. Blank entries are detected via isBlankEntry() below
  // and rendered with a "Fill In" action instead of the normal edit control.
  const [scanning, setScanning] = useState(false);

  const emptyForm: CreateLotteryRequest = {
    scratchTickets: '',
    looseSales: 0,
    bankStart: 0,
    bankEnd: 0,
    onlineSales: 0,
    onlinePdOut: 0,
    scratchPdOut: 0,
    moneyLeft: 0,
    notes: '',
    deposit: 0,
    safeWithdrawals: 0,
  };

  // Form state
  const [formData, setFormData] = useState<CreateLotteryRequest>(emptyForm);
  const [formLoading, setFormLoading] = useState(false);

  // Date of the entry currently being edited/filled in (null = adding a new entry for today)
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  // Entries dated today or yesterday (per business "current date") can be corrected
  const isWithinLastTwoDays = (dateString: string): boolean => {
    if (!currentDate) return false;
    const entryDate = dateString.slice(0, 10);
    const today = new Date(currentDate + 'T00:00:00Z');
    const yesterday = new Date(today);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    const yesterdayStr = yesterday.toISOString().slice(0, 10);
    return entryDate === currentDate || entryDate === yesterdayStr;
  };

  const fetchLotteryEntries = async (pageNum: number = 1): Promise<PaginatedLotteryResponse | undefined> => {
    if (!sessionToken) return;

    setLoading(true);

    try {
      const response: PaginatedLotteryResponse = await lotteryApi.getLotteryEntries(
        { page: pageNum, limit },
        sessionToken
      );

      setLotteryEntries(response.data);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
      setHasNext(response.hasNext);
      setHasPrev(response.hasPrev);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch lottery entries';
      showError(errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentDate = async () => {
    if (!sessionToken) return;
    
    try {
      setDateLoading(true);
      const response = await tipoutApi.getCurrentDate(sessionToken);
      setCurrentDate(response.date);
    } catch (error) {
      console.error('Error fetching current date:', error);
      showError('Failed to fetch current date');
    } finally {
      setDateLoading(false);
    }
  };

  useEffect(() => {
    fetchLotteryEntries();
    fetchCurrentDate();
  }, [sessionToken]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchLotteryEntries(value);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Scan a set of entries for any day with no entry, between the oldest and newest date
  // in that set (extended to today on page 1, generalizing the old "is yesterday missing"
  // check). Each gap gets a real blank entry created for it (see handleScanMissingDates).
  const computeMissingDates = (entries: Lottery[], pageNum: number, current: string): string[] => {
    if (entries.length === 0) return [];

    const existing = new Set(entries.map((e) => e.date.slice(0, 10)));
    const sortedAsc = [...existing].sort();
    const startDate = sortedAsc[0];
    let endDate = sortedAsc[sortedAsc.length - 1];
    if (pageNum === 1 && current && current > endDate) {
      endDate = current;
    }

    const missing: string[] = [];
    const cursor = new Date(startDate + 'T00:00:00Z');
    const end = new Date(endDate + 'T00:00:00Z');
    while (cursor <= end) {
      const dateStr = cursor.toISOString().slice(0, 10);
      if (!existing.has(dateStr)) missing.push(dateStr);
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return missing;
  };

  // A blank entry is one Scan created to mark a gap (every amount still $0, no
  // scratch tickets/notes) and hasn't been filled in yet. Safe-Bank End is
  // excluded here since it's derived from Safe Withdrawals, not an independent
  // signal of blankness (a blank entry's derived value is $2000, not $0).
  const isBlankEntry = (entry: Lottery): boolean => {
    return (
      !entry.scratchTickets &&
      !entry.notes &&
      Number(entry.looseSales) === 0 &&
      Number(entry.bankStart) === 0 &&
      Number(entry.onlineSales) === 0 &&
      Number(entry.onlinePdOut) === 0 &&
      Number(entry.scratchPdOut) === 0 &&
      Number(entry.moneyLeft) === 0 &&
      Number(entry.deposit) === 0 &&
      Number(entry.safeWithdrawals) === 0
    );
  };

  const handleScanMissingDates = async () => {
    if (!sessionToken) return;
    setScanning(true);
    try {
      const response = await fetchLotteryEntries(page);
      if (!response) return;

      const missing = computeMissingDates(response.data, response.page, currentDate);
      if (missing.length === 0) {
        showSuccess('No missing dates found on this page.');
        return;
      }

      await Promise.all(
        missing.map((date) =>
          lotteryApi.createLotteryEntry({ ...emptyForm, date, bankEnd: computeSafeBankEnd(0) }, sessionToken)
        )
      );
      showSuccess(
        `Added ${missing.length} blank entr${missing.length === 1 ? 'y' : 'ies'} for the missing date${missing.length === 1 ? '' : 's'}. Use "Fill In" to complete ${missing.length === 1 ? 'it' : 'them'}.`
      );
      await fetchLotteryEntries(page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to scan for missing dates';
      showError(errorMessage);
    } finally {
      setScanning(false);
    }
  };

  // Auto-saves ~900ms after the last change to any field, while correcting an existing
  // entry (editingDate set). Adding today's new entry still uses the explicit Add button.
  const performAutoSave = useCallback(
    async (date: string, snapshot: CreateLotteryRequest) => {
      if (!sessionToken) return;
      setAutoSaveStatus('saving');
      try {
        await lotteryApi.createLotteryEntry(
          { ...snapshot, date, bankEnd: computeSafeBankEnd(snapshot.safeWithdrawals || 0) },
          sessionToken
        );
        setAutoSaveStatus('saved');
        fetchLotteryEntries(page);
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken, page]
  );

  const handleFormChange = (field: keyof CreateLotteryRequest, value: string | number) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if ((DEPOSIT_SOURCE_FIELDS as readonly string[]).includes(field as string)) {
        next.deposit = computeDeposit(next);
      }
      if (editingDate) {
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        setAutoSaveStatus('pending');
        autoSaveTimeoutRef.current = setTimeout(() => {
          autoSaveTimeoutRef.current = null;
          performAutoSave(editingDate, next);
        }, 900);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionToken) return;

    setFormLoading(true);
    try {
      const payload: CreateLotteryRequest = {
        ...(editingDate ? { ...formData, date: editingDate } : formData),
        bankEnd: computeSafeBankEnd(formData.safeWithdrawals || 0),
      };
      await lotteryApi.createLotteryEntry(payload, sessionToken);
      showSuccess(editingDate ? 'Lottery entry updated successfully!' : 'Lottery entry created successfully!');

      // Reset form back to "new entry for today" mode
      setFormData(emptyForm);
      setEditingDate(null);

      // Refresh table
      fetchLotteryEntries(page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save lottery entry';
      showError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  // Load an existing entry's values into the form so it can be corrected
  const handleEditEntry = (entry: Lottery) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setFormData({
      date: entry.date.slice(0, 10),
      scratchTickets: entry.scratchTickets || '',
      looseSales: entry.looseSales,
      bankStart: entry.bankStart,
      bankEnd: entry.bankEnd,
      onlineSales: entry.onlineSales,
      onlinePdOut: entry.onlinePdOut,
      scratchPdOut: entry.scratchPdOut,
      moneyLeft: entry.moneyLeft,
      notes: entry.notes || '',
      deposit: entry.deposit,
      safeWithdrawals: entry.safeWithdrawals,
    });
    setEditingDate(entry.date.slice(0, 10));
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  // Load a missing day's placeholder into the form so it can be filled in
  const handleFillMissing = (date: string) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setFormData({ ...emptyForm, date });
    setEditingDate(date);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handleCancelEdit = async () => {
    if (autoSaveTimeoutRef.current && editingDate) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await performAutoSave(editingDate, formData);
    }
    setFormData(emptyForm);
    setEditingDate(null);
    setAutoSaveStatus('idle');
  };

  if (loading && lotteryEntries.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {dateLoading ? <CircularProgress size={16} sx={{ ml: 1 }} /> : currentDate ? formatDate(currentDate) : ''}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Lottery
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {isAdmin && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={scanning ? <CircularProgress size={16} /> : <ScanIcon />}
              onClick={handleScanMissingDates}
              disabled={scanning}
            >
              {scanning ? 'Scanning…' : 'Scan for Missing Dates'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchLotteryEntries(page)}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Records table */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {total} entr{total !== 1 ? 'ies' : 'y'} total{totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ''}
      </Typography>

      <Paper sx={{ width: '100%', overflow: 'hidden', border: LOTTERY_BORDER, mb: 4 }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', '& td': { border: LOTTERY_BODY_BORDER, py: 1.25, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {[
                  { label: 'Date', align: 'left' },
                  { label: 'Scratch Tickets', align: 'left' },
                  { label: 'Scratch Loose Sales', align: 'right' },
                  { label: 'Keno Online Sales', align: 'right' },
                  { label: 'Keno Cash PD Out', align: 'right' },
                  { label: 'Online PD out', align: 'right' },
                  { label: 'Bank Deposit', align: 'right' },
                  { label: 'Cash Dep. Withdrawals', align: 'right' },
                  { label: 'Safe Withdrawals', align: 'right' },
                  { label: 'Safe Lottery Amt', align: 'right' },
                  { label: 'Notes', align: 'left' },
                  { label: 'Actions', align: 'center' },
                ].map(({ label }, i) => (
                  <TableCell
                    key={label}
                    align="center"
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      lineHeight: 1.25,
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                      border: LOTTERY_BORDER,
                      py: 1,
                      px: 0.75,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      width: LOTTERY_COLUMN_WIDTHS[i],
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lotteryEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 5, border: LOTTERY_BODY_BORDER, color: 'text.secondary' }}>
                    No lottery entries yet. Use the form below to add one.
                  </TableCell>
                </TableRow>
              ) : (
                lotteryEntries.map((entry, idx) => {
                  const entryDateStr = entry.date.slice(0, 10);
                  const isBlank = isBlankEntry(entry);
                  // Flag entries with no scratch tickets recorded and zero online sales —
                  // worth a second look even though the entry isn't fully blank.
                  const needsAttention = !isBlank && !entry.scratchTickets && Number(entry.onlineSales) === 0;
                  const editable = isBlank || isWithinLastTwoDays(entryDateStr);
                  const isEditingThis = editingDate === entryDateStr;
                  const cellBorder = isBlank ? { border: LOTTERY_MISSING_BORDER } : undefined;
                  return (
                    <TableRow
                      key={entry.id}
                      sx={{
                        backgroundColor: isBlank
                          ? (isEditingThis ? '#ffe0b2' : '#fff8e1')
                          : needsAttention
                            ? (isEditingThis ? '#fff59d' : '#fff9c4')
                            : isEditingThis ? '#fce4e4' : idx % 2 === 0 ? '#fff' : '#fdf5f5',
                        '&:hover': { backgroundColor: isBlank ? '#ffe0b2' : needsAttention ? '#fff59d' : '#fce4e4' },
                      }}
                    >
                      <TableCell sx={cellBorder}>
                        <Chip
                          label={formatDate(entry.date)}
                          variant={isBlank ? 'filled' : 'outlined'}
                          size="small"
                          sx={isBlank
                            ? { borderRadius: 1, backgroundColor: '#ffe082', color: '#5d4037', fontWeight: 700, border: '2px solid #ffb300' }
                            : { borderRadius: 1 }}
                        />
                        {isBlank && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#e65100', fontWeight: 600, mt: 0.25 }}>
                            Missing entry
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>
                        {isBlank ? '—' : entry.scratchTickets ? `$${formatCurrency(Number(entry.scratchTickets) || 0)}` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>{isBlank ? '—' : `$${formatCurrency(entry.looseSales)}`}</TableCell>
                      <TableCell align="right" sx={{ ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>{isBlank ? '—' : `$${formatCurrency(entry.onlineSales)}`}</TableCell>
                      <TableCell align="right" sx={{ ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>{isBlank ? '—' : `$${formatCurrency(entry.onlinePdOut)}`}</TableCell>
                      <TableCell align="right" sx={{ ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>{isBlank ? '—' : `$${formatCurrency(entry.scratchPdOut)}`}</TableCell>
                      <TableCell align="right" sx={{ ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>{isBlank ? '—' : `$${formatCurrency(entry.deposit)}`}</TableCell>
                      <TableCell align="right" sx={{ ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>{isBlank ? '—' : `$${formatCurrency(entry.moneyLeft)}`}</TableCell>
                      <TableCell align="right" sx={{ ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>{isBlank ? '—' : `$${formatCurrency(entry.safeWithdrawals)}`}</TableCell>
                      <TableCell align="right" sx={{ ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>{isBlank ? '—' : `$${formatCurrency(entry.bankEnd)}`}</TableCell>
                      <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...cellBorder, ...(isBlank ? { color: '#bdbdbd' } : {}) }}>
                        {isBlank ? '—' : entry.notes || '—'}
                      </TableCell>
                      <TableCell align="center" sx={cellBorder}>
                        {isBlank ? (
                          <Tooltip title="Fill in this missing entry">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleFillMissing(entryDateStr)}
                              disabled={isEditingThis}
                              sx={{ backgroundColor: '#ffb300', color: '#5d4037', '&:hover': { backgroundColor: '#ffa000' } }}
                            >
                              {isEditingThis ? 'Editing…' : 'Fill In'}
                            </Button>
                          </Tooltip>
                        ) : editable ? (
                          <Tooltip title={isEditingThis ? 'Currently editing' : 'Update this entry'}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditEntry(entry)}
                                disabled={isEditingThis}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Only entries from the last 2 days can be updated">
                            <span>
                              <IconButton size="small" disabled>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      {/* Add New Entry Form */}
      <Paper sx={{ border: LOTTERY_BORDER, overflow: 'hidden' }}>
        {/* Form header */}
        <Box sx={{ backgroundColor: editingDate ? '#7f0000' : '#b71c1c', px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700} color="#fff" letterSpacing="0.04em" textTransform="uppercase" fontSize="0.82rem">
            {editingDate ? `Update Entry — ${formatDate(editingDate)}` : 'Add New Lottery Entry'}
          </Typography>
          {editingDate ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="caption" sx={{ color: autoSaveStatus === 'error' ? '#ffcdd2' : 'rgba(255,255,255,0.85)' }}>
                {autoSaveStatus === 'saving' && 'Saving…'}
                {autoSaveStatus === 'pending' && 'Unsaved changes…'}
                {autoSaveStatus === 'saved' && 'All changes saved'}
                {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
              </Typography>
              <Button
                size="small"
                onClick={handleCancelEdit}
                startIcon={<CloseIcon fontSize="small" />}
                sx={{ color: '#fff' }}
              >
                Done
              </Button>
            </Box>
          ) : dateLoading ? (
            <CircularProgress size={16} sx={{ color: '#fff' }} />
          ) : (
            <Typography variant="body2" color="rgba(255,255,255,0.85)">
              {currentDate ? formatDate(currentDate) : ''}
            </Typography>
          )}
        </Box>

        <Box sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField size="small" label="Scratch Tickets" type="number" inputProps={{ step: '0.01' }}
                value={formData.scratchTickets || ''} placeholder="Optional"
                onChange={(e) => handleFormChange('scratchTickets', e.target.value)} disabled={formLoading}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                sx={{ flex: '1 1 150px' }} />
              <TextField size="small" label="Scratch Loose Sales" type="number" inputProps={{ step: '0.01' }} value={formData.looseSales || ''}
                onChange={(e) => handleFormChange('looseSales', parseFloat(e.target.value) || 0)} disabled={formLoading}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                sx={{ flex: '1 1 110px' }} />
              <TextField size="small" label="Keno Online Sales" type="number" inputProps={{ step: '0.01' }} value={formData.onlineSales || ''}
                onChange={(e) => handleFormChange('onlineSales', parseFloat(e.target.value) || 0)} disabled={formLoading}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                sx={{ flex: '1 1 110px' }} />
              <TextField size="small" label="Keno Cash PD Out" type="number" inputProps={{ step: '0.01' }} value={formData.onlinePdOut || ''}
                onChange={(e) => handleFormChange('onlinePdOut', parseFloat(e.target.value) || 0)} disabled={formLoading}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                sx={{ flex: '1 1 110px' }} />
              <TextField size="small" label="Online PD out" type="number" inputProps={{ step: '0.01' }} value={formData.scratchPdOut || ''}
                onChange={(e) => handleFormChange('scratchPdOut', parseFloat(e.target.value) || 0)} disabled={formLoading}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                sx={{ flex: '1 1 110px' }} />
              <TextField size="small" label="Bank Deposit" type="number" inputProps={{ step: '0.01' }} value={formData.deposit || ''}
                onChange={(e) => handleFormChange('deposit', parseFloat(e.target.value) || 0)} disabled={formLoading}
                helperText="Auto-calculated; edit to override"
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                sx={{ flex: '1 1 110px' }} />
              <TextField size="small" label="Cash Dep. Withdrawals" type="number" inputProps={{ step: '0.01' }} value={formData.moneyLeft || ''}
                onChange={(e) => handleFormChange('moneyLeft', parseFloat(e.target.value) || 0)} disabled={formLoading}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                sx={{ flex: '1 1 110px' }} />
              <TextField size="small" label="Safe Withdrawals" type="number" inputProps={{ step: '0.01' }} value={formData.safeWithdrawals || '0.00'}
                onChange={(e) => handleFormChange('safeWithdrawals', parseFloat(e.target.value) || 0)} disabled={formLoading}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                sx={{ flex: '1 1 110px' }} />
              <TextField size="small" label="Safe Lottery Amt" type="number"
                value={computeSafeBankEnd(formData.safeWithdrawals || 0).toFixed(2)}
                disabled
                helperText={`$${SAFE_START_AMOUNT} − Safe Withdrawals`}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>, readOnly: true }}
                sx={{ flex: '1 1 110px' }} />
              <TextField size="small" label="Notes" value={formData.notes || ''} placeholder="Optional"
                onChange={(e) => handleFormChange('notes', e.target.value)} disabled={formLoading}
                sx={{ flex: '1 1 150px' }} />
              <Button type="submit" variant="contained" startIcon={formLoading ? <CircularProgress size={16} color="inherit" /> : editingDate ? <EditIcon /> : <AddIcon />}
                disabled={formLoading}
                sx={{ flex: '0 0 auto', alignSelf: 'center', backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}>
                {editingDate ? 'Update' : 'Add'}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default LotteryPage;

